import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Library, Loader2, Trash2, UserPlus, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getCollections, updateCollection, deleteCollection } from '@/lib/api/collections/get-collections'
import {
  getMembers,
  getUsers,
  shareCollection,
  unshareCollection,
  type Member,
} from '@/lib/api/auth/users'
import { apiErrorMessage } from '@/lib/api-client'
import type { Collection } from '@/types/terms'

const ROLES = ['viewer', 'editor', 'owner'] as const

/** Every collection, with who can reach it. Admin only. */
export function CollectionsSection() {
  const { t } = useTranslation()
  const collections = useQuery({ queryKey: ['collections'], queryFn: getCollections })

  return (
    <section className="rounded-md border border-border p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-medium">
        <Library className="size-4 text-muted-foreground" />
        {t('settings.collections.section')}
        <Badge variant="primary" size="sm">
          {t('auth.admin')}
        </Badge>
      </h3>

      <div className="space-y-2">
        {collections.data?.map((c) => <CollectionRow key={c.id} collection={c} />)}
        {!collections.data?.length && (
          <p className="text-xs text-muted-foreground">{t('settings.collections.empty')}</p>
        )}
      </div>
    </section>
  )
}

function CollectionRow({ collection }: { collection: Collection }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(collection.name)
  const [description, setDescription] = useState(collection.description ?? '')
  const [confirm, setConfirm] = useState<string | null>(null)

  const dirty = name !== collection.name || description !== (collection.description ?? '')

  const save = useMutation({
    mutationFn: () => updateCollection(collection.id, { name, description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collections'] })
      toast.success(t('settings.collections.saved'))
    },
    onError: (e) =>
      toast.error(t('settings.collections.saveFailed'), { description: apiErrorMessage(e) }),
  })

  const destroy = useMutation({
    mutationFn: () => deleteCollection(collection.id, confirm ?? ''),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['collections'] })
      setConfirm(null)
      toast.success(t('settings.collections.deleted', { name: res.name }), {
        description: t('settings.collections.deletedCounts', {
          documents: res.documents_deleted,
          chunks: res.chunks_deleted,
        }),
      })
    },
    onError: (e) =>
      toast.error(t('settings.collections.deleteFailed'), { description: apiErrorMessage(e) }),
  })

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/40"
      >
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{collection.name}</span>
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {t('settings.collections.counts', {
            documents: collection.document_count,
            terms: collection.term_count,
            concepts: collection.concept_count,
          })}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-border px-3 py-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-40 flex-1 text-[11px] text-muted-foreground">
              {t('settings.collections.name')}
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
            </label>
            <label className="min-w-40 flex-[2] text-[11px] text-muted-foreground">
              {t('settings.collections.description')}
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1"
              />
            </label>
            <Button
              size="sm"
              disabled={!dirty || !name.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending && <Loader2 className="mr-1 size-3.5 animate-spin" />}
              {t('common.save')}
            </Button>
          </div>

          <MemberList collectionId={collection.id} />

          {confirm === null ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-danger hover:bg-danger/10"
              onClick={() => setConfirm('')}
            >
              <Trash2 className="mr-1 size-3.5" />
              {t('settings.collections.delete')}
            </Button>
          ) : (
            <div className="rounded-md border border-danger/40 bg-danger/5 p-2.5">
              {/* The counts are the warning: this removes the PDFs and the
                  vector chunks too, and none of it comes back. */}
              <p className="text-[11px] text-muted-foreground">
                {t('settings.collections.deleteCounts', {
                  documents: collection.document_count,
                  terms: collection.term_count,
                  concepts: collection.concept_count,
                })}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Input
                  value={confirm}
                  autoFocus
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={collection.name}
                  className="h-8 max-w-64 flex-1"
                />
                <Button
                  size="sm"
                  variant="danger"
                  disabled={confirm.trim() !== collection.name.trim() || destroy.isPending}
                  onClick={() => destroy.mutate()}
                >
                  {destroy.isPending && <Loader2 className="mr-1 size-3.5 animate-spin" />}
                  {t('settings.collections.deleteConfirm')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function MemberList({ collectionId }: { collectionId: number }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [role, setRole] = useState<string>('viewer')
  const [query, setQuery] = useState('')

  const members = useQuery({
    queryKey: ['members', collectionId],
    queryFn: () => getMembers(collectionId),
  })
  const users = useQuery({ queryKey: ['users'], queryFn: getUsers })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['members', collectionId] })

  const share = useMutation({
    mutationFn: ({ userId, role: r }: { userId: number; role: string }) =>
      shareCollection(collectionId, userId, r),
    onSuccess: () => {
      invalidate()
      setQuery('')
    },
    onError: (e) => toast.error(t('share.shareFailed'), { description: apiErrorMessage(e) }),
  })
  const revoke = useMutation({
    mutationFn: (userId: number) => unshareCollection(collectionId, userId),
    onSuccess: invalidate,
    onError: (e) => toast.error(t('share.revokeFailed'), { description: apiErrorMessage(e) }),
  })

  const taken = new Set((members.data ?? []).map((m: Member) => m.user_id))
  const candidates = (users.data ?? []).filter(
    (u) =>
      !taken.has(u.id) &&
      (query.trim() === '' ||
        `${u.name} ${u.username} ${u.email}`.toLowerCase().includes(query.toLowerCase())),
  )

  return (
    <div className="rounded-md border border-border">
      <div className="divide-y divide-border/60">
        {members.data?.map((m) => (
          <div key={m.user_id} className="flex items-center gap-2 px-2.5 py-1.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px]">{m.name || m.username}</p>
              <p className="truncate text-[10px] text-muted-foreground">{m.username}</p>
            </div>
            <select
              value={m.role}
              onChange={(e) => share.mutate({ userId: m.user_id, role: e.target.value })}
              className="h-7 rounded border border-border bg-background px-1.5 text-[11px]"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`roles.${r}` as const)}
                </option>
              ))}
            </select>
            <Button
              size="icon-sm"
              variant="ghost"
              title={t('share.revoke')}
              onClick={() => revoke.mutate(m.user_id)}
            >
              <X className="size-3.5" />
            </Button>
          </div>
        ))}
        {!members.data?.length && (
          <p className="px-2.5 py-1.5 text-[11px] text-muted-foreground">
            {t('settings.collections.noMembers')}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-border px-2.5 py-2">
        <UserPlus className="size-3.5 shrink-0 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('share.searchUsers')}
          className="h-7 max-w-48 flex-1 text-[11px]"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="h-7 rounded border border-border bg-background px-1.5 text-[11px]"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {t(`roles.${r}` as const)}
            </option>
          ))}
        </select>
        {query.trim() !== '' && (
          <div className="flex w-full flex-wrap gap-1">
            {candidates.slice(0, 6).map((u) => (
              <Button
                key={u.id}
                size="sm"
                variant="outline"
                onClick={() => share.mutate({ userId: u.id, role })}
              >
                {u.name || u.username}
              </Button>
            ))}
            {!candidates.length && (
              <span className="text-[11px] text-muted-foreground">{t('common.noMatches')}</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
